import { observer } from "mobx-react"
import { useMemo, useRef, useState } from "react"
import { isHarnessId } from "../../modelRegistry"
import { SessionThreadModel } from "../../store/SessionThreadModel"
import type { TaskModel } from "../../store/TaskModel"
import type { TaskProviderSessionReference } from "../../types"
import { SessionThreadView } from "../SessionThreadView"

function EarlierConversation({ session, taskModel }: { session: TaskProviderSessionReference; taskModel: TaskModel }) {
    const [open, setOpen] = useState(false)
    return (
        <details onToggle={(event) => setOpen(event.currentTarget.open)} className="border-b border-border">
            <summary className="cursor-pointer px-5 py-3 text-sm text-muted">Earlier conversation · {session.model ?? session.harnessId}</summary>
            {open && <EarlierThread session={session} taskModel={taskModel} />}
        </details>
    )
}

function EarlierThread({ session, taskModel }: { session: TaskProviderSessionReference; taskModel: TaskModel }) {
    const scrollRef = useRef<HTMLDivElement>(null)
    const model = useMemo(
        () =>
            session.nestSessionId && isHarnessId(session.harnessId)
                ? new SessionThreadModel({
                      sessionId: session.nestSessionId,
                      harnessId: session.harnessId,
                      cwd: session.cwd,
                      model: session.model,
                  })
                : null,
        [session.nestSessionId, session.harnessId, session.cwd, session.model]
    )
    return (
        <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto border-t border-border bg-base-100">
            {model ? (
                <SessionThreadView
                    model={model}
                    taskId={taskModel.id}
                    taskSessions={taskModel.sessionDebugInfo}
                    scrollElementRef={scrollRef}
                    initialScrollToBottom
                />
            ) : (
                <p className="p-5 text-sm text-muted">This session's history is unavailable.</p>
            )}
        </div>
    )
}

export const TaskHandoffHistory = observer(function TaskHandoffHistory({ taskModel }: { taskModel: TaskModel }) {
    const sessions = taskModel.providerSessions
    const current = sessions.find((session) => session.nestSessionId === taskModel.primarySessionThreadReference?.sessionId)
    const byId = new Map(sessions.map((session) => [session.nestSessionId, session]))
    const previous: TaskProviderSessionReference[] = []
    const seen = new Set<string>()
    let predecessor = current?.predecessorSessionId
    while (predecessor && !seen.has(predecessor)) {
        seen.add(predecessor)
        const session = byId.get(predecessor)
        if (!session) break
        previous.unshift(session)
        predecessor = session.predecessorSessionId
    }
    if (!current?.predecessorSessionId) return null
    return (
        <div aria-label="Handoff history">
            {!current.startTurnId && !current.lastTurnId && !taskModel.handoffSubmitting && (
                <div role="status" className="border-b border-warning/40 bg-warning/10 px-5 py-3 text-sm text-base-content">
                    Replacement startup was not confirmed. Inspect this session before continuing.
                </div>
            )}
            {previous.map((session) => (
                <EarlierConversation key={session.nestSessionId} session={session} taskModel={taskModel} />
            ))}
            <div className="border-b border-border bg-base-200 px-5 py-3 text-xs text-muted">
                Handed off to <span className="font-semibold text-base-content">{current.model ?? current.harnessId}</span> · This agent now leads the task
            </div>
        </div>
    )
})
